import React, { useEffect, useRef, useState } from 'react';
import {
  IconX,
  IconNote,
  IconPhoto,
  IconCamera,
  IconFileText,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canAttachPackingSlip } from '../../lib/permissions';
import StoredImage from '../../components/StoredImage';
import { openStoredFile, INVENTORY_BUCKET } from '../../lib/storedFile';
import { ledgerCostState, movementNote } from '../../lib/ledgerExport';
import { CORRECTED_LABEL, isCorrectionTakeBack } from '../../lib/corrections';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

const TX_TYPE_STYLES = {
  receive:      { bg: '#E1F5EE', color: '#085041', label: 'Receive' },
  consume:      { bg: '#FAECE7', color: '#712B13', label: 'Consume' },
  adjust:       { bg: '#EEEDFE', color: '#3C3489', label: 'Adjust' },
  transfer_in:  { bg: '#E6F1FB', color: '#0C447C', label: 'Transfer in' },
  transfer_out: { bg: '#FAEEDA', color: '#633806', label: 'Transfer out' },
  production_in: { bg: '#E1F5EE', color: '#085041', label: 'Made' },   // #371 D23
  reversal:     { bg: '#F1EFE8', color: '#5F5E5A', label: 'Undone' },  // #694
};

function fmtDateTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtQty(qty, txType) {
  if (qty === null || qty === undefined) return '—';
  const n = Number(qty);
  if (txType === 'receive' || txType === 'transfer_in' || txType === 'production_in') return `+${Math.abs(n)}`;
  if (txType === 'consume' || txType === 'transfer_out') return `−${Math.abs(n)}`;
  // An Undo carries its own sign, like adjust (#694).
  if (txType === 'adjust' || txType === 'reversal') return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
  return String(n);
}

function getQtyColor(qty, txType) {
  if (txType === 'receive' || txType === 'transfer_in' || txType === 'production_in') return '#0F6E56';
  if (txType === 'consume' || txType === 'transfer_out') return '#A32D2D';
  if (txType === 'adjust' || txType === 'reversal') return Number(qty) >= 0 ? '#0F6E56' : '#A32D2D';
  return '#0F0F0F';
}

function DetailRow({ label, value, valueStyle }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '8px 0', borderBottom: '1px solid #F0F0F0',
    }}>
      <div style={{ width: 110, flexShrink: 0, fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', paddingTop: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#0F0F0F', fontWeight: 500, flex: 1, ...valueStyle }}>
        {value}
      </div>
    </div>
  );
}

// `canSeeCost` is the Ledger's own answer, passed down rather than worked out
// again here (#624): the panel shows money exactly when the list does.
export default function TransactionDetailPanel({ tx, onClose, isMobile, canSeeCost = false }) {
  const panelRef = useRef();

  // #504, Session 79.1 - attaching a packing slip to a receive that has none.
  //
  // Hooks are declared BEFORE the `if (!tx) return null` below, deliberately:
  // React requires the same hooks on every render, and putting them after an
  // early return is how a component starts throwing the moment the panel closes.
  const auth = useAuth();
  const cameraRef = useRef();
  const pdfRef = useRef();
  const [slipUrl, setSlipUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Reset when a different transaction is opened, or the previous receipt's
  // freshly-attached slip would appear on the next one.
  useEffect(() => { setSlipUrl(null); setUploadError(''); setUploading(false); }, [tx?.id]);

  async function attachSlip(file) {
    if (!file || !tx) return;
    setUploadError('');
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `packing-slips/late-tx${tx.id}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('inventory-assets')
        .upload(path, file, { upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: urlData } = supabase.storage.from('inventory-assets').getPublicUrl(path);
      const url = urlData?.publicUrl || null;
      if (!url) throw new Error('The file uploaded but no address came back for it.');
      // #504 — READ THE ROW BACK. `error` being null is NOT proof the write
      // happened.
      //
      // This is the defect this control shipped with for about ten minutes, and
      // it is worth the comment because it is the exact class Session 79.1 exists
      // to remove. `inventory_transactions` has row-level security on and had no
      // UPDATE policy, so this statement matched ZERO rows — and PostgREST
      // reports zero rows changed as SUCCESS with no error. The screen said
      // "Added just now", hid its own button, and had written nothing. The
      // migration 20260906_attach_late_packing_slip.sql fixes the permission;
      // this fixes the far more dangerous half, which is that the screen could
      // not tell the difference.
      //
      // `.select()` makes the statement return the rows it changed, so an empty
      // array is a failure we can see. Any future policy change that silently
      // stops this working surfaces as an error to the person instead of a lie.
      const { data: updated, error: updErr } = await supabase
        .from('inventory_transactions')
        .update({ packing_slip_url: url })
        .eq('id', tx.id)
        .select('id, packing_slip_url');
      if (updErr) throw new Error(updErr.message);
      if (!updated || updated.length === 0) {
        throw new Error('The file uploaded, but this receipt could not be updated — you may not have permission to change it.');
      }
      setSlipUrl(updated[0].packing_slip_url || url);
    } catch (e) {
      setUploadError(e.message || 'The upload did not work. Try again.');
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!tx) return null;

  // #761 / gate B6 -- a take-back a correction wrote reads Corrected.
  const baseTs = TX_TYPE_STYLES[tx.tx_type] || { bg: '#F0F0F0', color: '#5C5C5C', label: tx.tx_type };
  const ts = isCorrectionTakeBack(tx) ? { ...baseTs, label: CORRECTED_LABEL } : baseTs;
  const branchName = BRANCHES.find(b => b.id === tx.branch_id)?.name || '—';

  // #504. Offered only on a RECEIVE that has no slip yet, and only to somebody
  // entitled: the person who recorded it, a manager at that branch, or accounts
  // payable - who ends up holding every slip anyway. ADD-ONLY: a slip already on
  // the record is evidence, and replacing evidence needs an audit trail this app
  // does not have. The predicate is asked here rather than inlined, per locked
  // rule 8.
  const existingSlip = slipUrl || tx.packing_slip_url;
  const canAttach = tx.tx_type === 'receive' && !existingSlip && canAttachPackingSlip(auth, tx);
  const qtyColor = getQtyColor(tx.qty, tx.tx_type);

  const content = (
    <div ref={panelRef} style={{
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '16px 20px 12px' : '14px 20px 12px',
        borderBottom: '1px solid #E8E8E8', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 20,
            fontSize: 11, fontWeight: 700, background: ts.bg, color: ts.color,
          }}>
            {ts.label}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>
            {fmtDateTime(tx.created_at)}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 28, height: 28, border: 'none', background: '#F0F0F0',
            borderRadius: '50%', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <IconX size={14} color="#5C5C5C" />
        </button>
      </div>

      {/* Qty hero */}
      <div style={{
        padding: '16px 20px', background: '#F8F8F6',
        borderBottom: '1px solid #E8E8E8', flexShrink: 0,
      }}>
        <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Quantity</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 32, fontWeight: 700, color: qtyColor, lineHeight: 1 }}>
            {fmtQty(tx.qty, tx.tx_type)}
          </span>
          {tx.qty_after !== null && tx.qty_after !== undefined && (
            <span style={{ fontSize: 13, color: '#7A7A7A' }}>
              → <strong style={{ color: '#0F0F0F' }}>{tx.qty_after}</strong> on hand after
            </span>
          )}
        </div>
      </div>

      {/* Detail rows */}
      <div style={{ padding: '4px 20px 20px', overflowY: 'auto', flex: 1 }}>

        <DetailRow
          label="Item"
          value={
            tx.inventory_items ? (
              <span>
                <span style={{ fontWeight: 700 }}>{tx.inventory_items.name}</span>
                {tx.inventory_items.sku && (
                  <span style={{ fontSize: 11, color: '#7A7A7A', marginLeft: 8 }}>
                    {tx.inventory_items.sku}
                  </span>
                )}
              </span>
            ) : '—'
          }
        />

        <DetailRow label="Branch" value={branchName} />

        <DetailRow
          label="User"
          value={tx.users?.full_name || '—'}
        />

        <DetailRow
          label="WO#"
          value={tx.work_order_ref || '—'}
          valueStyle={tx.work_order_ref ? { fontFamily: 'monospace', fontSize: 12 } : { color: '#9A9A9A' }}
        />

        {/* #633 -- the document and the other branch, which the list names too.
            Each row draws only when the movement carries it. */}
        <DetailRow
          label="Transfer"
          value={tx.transfer_number || null}
          valueStyle={{ fontFamily: 'monospace', fontSize: 12 }}
        />
        <DetailRow
          label={tx.tx_type === 'transfer_in' ? 'From' : tx.tx_type === 'production_in' ? 'Made for' : 'To'}
          value={tx.counterparty_branch_id != null
            ? (BRANCHES.find(b => b.id === Number(tx.counterparty_branch_id))?.name || null)
            : null}
        />
        <DetailRow
          label="Request"
          value={tx.request_number || null}
          valueStyle={{ fontFamily: 'monospace', fontSize: 12 }}
        />

        {/* #788 (approved drawing D, D6) -- an adjustment's reason, note and
            "Adjusted by" were saved in the other column and never shown here. */}
        <DetailRow
          label="Notes"
          value={
            movementNote(tx) ? (
              <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <IconNote size={13} color="#7A7A7A" style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{movementNote(tx)}</span>
              </span>
            ) : null
          }
        />

        {/* #624 -- this was a placeholder that printed a dash for EVERY
            movement, priced or not, so a delivery booked at $4.75 opened a
            panel claiming it had no cost. Now the list's own rule, in words,
            to the people the list shows cost to - and no row for anybody else. */}
        {canSeeCost && (() => {
          const c = ledgerCostState(tx);
          // The rule's sentences were written for a DELIVERY - "assumed at
          // receive", "no vendor invoice has settled it yet" - and are false of
          // a transfer or a run, which no invoice ever settles. Found on screen
          // on SAT-TRF-00003. So the sentence shows where it is true; anywhere
          // else the panel says what the list says. Wording by movement type is
          // #624's held decision, not taken here.
          const says = c.state === 'none' || tx.tx_type === 'receive'
            ? c.title
            : (c.state === 'settled' ? 'Settled' : 'Provisional');
          return (
            <DetailRow
              label="Cost"
              value={
                <span style={{ display: 'block' }}>
                  {c.state !== 'none' && (
                    <span style={{ display: 'block', fontWeight: 700, color: '#0F0F0F' }}>{c.text}</span>
                  )}
                  <span style={{ display: 'block', color: '#5C5C5C', fontSize: 12, marginTop: c.state !== 'none' ? 2 : 0 }}>{says}</span>
                </span>
              }
            />
          );
        })()}

        {/* Packing slip — only if present */}
        {existingSlip && (
          <div style={{ paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
              Packing slip
            </div>
            {/* #536. Was a plain link carrying a world-readable address. It is a
                click handler now, because the address is fetched AT the click and
                lasts an hour - so nothing long-lived sits in the page waiting to
                be copied out, and a slip opened tomorrow still works. */}
            <div
              onClick={() => openStoredFile(existingSlip, INVENTORY_BUCKET)}
              style={{ display: 'block', textDecoration: 'none', cursor: 'pointer' }}
            >
              <StoredImage
                value={existingSlip}
                bucket={INVENTORY_BUCKET}
                alt="Packing slip"
                style={{
                  width: '100%', maxHeight: 200, objectFit: 'cover',
                  borderRadius: 8, border: '1px solid #E8E8E8', display: 'block',
                }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div style={{ fontSize: 11, color: '#1D9E75', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconPhoto size={12} />
                Tap to open full image
              </div>
            </div>
            {slipUrl && (
              <div style={{ fontSize: 11, color: '#2E7D32', marginTop: 6 }}>
                Added just now.
              </div>
            )}
          </div>
        )}

        {/* #504 — add a packing slip that was missed at receiving.
            Until Session 79.1 a slip could only ever be attached at the instant
            of receiving. If the driver had not handed it over, or the photo
            failed on shop wifi (#497), or somebody simply forgot, that receipt
            could never carry one — there was no way to add a document to
            anything after the fact anywhere in Stede.
            CAMERA ON MOBILE, PDF ON DESKTOP, per Colby's instruction. Both are
            offered on both, because the surface does not decide what is in
            somebody's hand — a phone can hold a PDF a vendor emailed, and a
            desktop can be a tablet on a bench. `capture="environment"` opens the
            rear camera directly rather than a file browser, which is the same
            treatment the receive sheet gives it. */}
        {canAttach && (
          <div style={{ paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
              Packing slip
            </div>
            <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 10, lineHeight: 1.45 }}>
              No packing slip on this receipt. You can add one now.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none',
                  background: uploading ? '#E0E0E0' : '#1D9E75', color: uploading ? '#9A9A9A' : 'white',
                  fontSize: 13, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <IconCamera size={15} color={uploading ? '#9A9A9A' : 'white'} />
                {uploading ? 'Uploading...' : (isMobile ? 'Take photo' : 'Add photo')}
              </button>
              <button
                onClick={() => pdfRef.current?.click()}
                disabled={uploading}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10,
                  border: '1px solid #E0E0E0', background: 'white', color: uploading ? '#9A9A9A' : '#0F0F0F',
                  fontSize: 13, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <IconFileText size={15} color={uploading ? '#9A9A9A' : '#5C5C5C'} />
                Attach a PDF
              </button>
            </div>
            {uploadError && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, fontSize: 11, color: '#A32D2D', marginTop: 8 }}>
                <IconAlertTriangle size={12} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{uploadError} Nothing was changed on this receipt.</span>
              </div>
            )}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              onChange={e => { attachSlip(e.target.files[0]); e.target.value = ''; }} />
            <input ref={pdfRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
              onChange={e => { attachSlip(e.target.files[0]); e.target.value = ''; }} />
          </div>
        )}
      </div>
    </div>
  );

  // ── MOBILE — bottom sheet ──────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
        />
        {/* Sheet */}
        <div style={{
          position: 'relative', background: 'white',
          borderRadius: '16px 16px 0 0', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />
          {content}
        </div>
      </div>
    );
  }

  // ── DESKTOP — slide-in panel ───────────────────────────────────────────────

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', justifyContent: 'flex-end' }}>
      {/* Dim backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }}
      />
      {/* Panel */}
      <div style={{
        position: 'relative', width: 400, background: 'white',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {content}
      </div>
    </div>
  );
}

// ── NOTE ICON HELPER ──────────────────────────────────────────────────────────
// Import and use this in table rows where work_order_ref_note is populated.
// Example: {tx.work_order_ref_note && <TxNoteIcon />}

export function TxNoteIcon() {
  return (
    <IconNote
      size={13}
      color="#7A7A7A"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4, flexShrink: 0 }}
    />
  );
}